import {
  RemoveDiscountCodeFailedAction,
  RemoveDiscountCodeFailedPayload,
  RemoveDiscountCodeFromCartsRequestedAction,
  RemoveDiscountCodeSucceededAction,
  RemoveDiscountCodeSucceededPayload,
  removeDiscountCodeFailedType,
  removeDiscountCodeFromCartsFailed,
  removeDiscountCodeFromCartsSucceeded,
  removeDiscountCodeRequested,
  removeDiscountCodeSucceededType
} from '../../carts/cartsSlice';
import { StateCart, StateCartDiscountCode, StatusEnvelop } from '@spa/lib/store/carts/CartsState';
import { put, select } from 'typed-redux-saga';
import { CustomOrderType } from '@spa/lib/store/carts/CustomOrderType';
import generateActionId from '@spa/lib/generateActionId';
import selectCarts from '@spa/lib/store/carts/selectors/selectCarts';
import waitForSucceededOrFailedAction from '@spa/lib/store/sagas/session/waitForSucceededOrFailedAction';

function* removeDiscountCodeFromCartIfPresent(
  customOrderType: CustomOrderType,
  codeId: string
): Generator<
  unknown,
  StatusEnvelop<RemoveDiscountCodeSucceededPayload, RemoveDiscountCodeFailedPayload> | null
> {
  const cart: StateCart | null = (yield* select(selectCarts))[customOrderType];
  let result: StatusEnvelop<
    RemoveDiscountCodeSucceededPayload,
    RemoveDiscountCodeFailedPayload
  > | null = null;
  const redeemedCode: StateCartDiscountCode | undefined = cart?.discountCodes?.find(
    ({ id }) => id === codeId
  );

  if (cart !== null && redeemedCode !== undefined) {
    const actionId: number = generateActionId();
    yield* put(
      removeDiscountCodeRequested({
        codeId,
        actionId,
        customOrderType
      })
    );

    const waitResult = yield* waitForSucceededOrFailedAction<
      typeof removeDiscountCodeSucceededType,
      typeof removeDiscountCodeFailedType,
      RemoveDiscountCodeSucceededAction,
      RemoveDiscountCodeFailedAction
    >(actionId, removeDiscountCodeSucceededType, removeDiscountCodeFailedType);

    if (waitResult.type === 'succeeded') {
      result = {
        succeeded: true,
        result: waitResult.action.payload
      };
    } else if (waitResult.type === 'failed') {
      result = {
        succeeded: false,
        result: waitResult.action.payload
      };
    }
  }

  return result;
}

function* removeDiscountCodeFromCartsSaga({
  payload: { requestedActionId, codeId }
}: RemoveDiscountCodeFromCartsRequestedAction): Generator {
  const cartResults = [
    {
      type: CustomOrderType.HOME_DELIVERY,
      result: yield* removeDiscountCodeFromCartIfPresent(CustomOrderType.HOME_DELIVERY, codeId)
    },
    {
      type: CustomOrderType.COLLECT_AT_STORE,
      result: yield* removeDiscountCodeFromCartIfPresent(CustomOrderType.COLLECT_AT_STORE, codeId)
    }
  ];

  const successfulRemovals: Array<CustomOrderType> = cartResults
    .filter(({ result }) => result?.succeeded === true)
    .map(({ type }) => type);

  const failedRemovals: Array<CustomOrderType> = cartResults
    .filter(({ result }) => result?.succeeded === false)
    .map(({ type }) => type);

  yield* put(
    failedRemovals.length > 0
      ? removeDiscountCodeFromCartsFailed({
          requestedActionId,
          codeId,
          customOrderTypes: failedRemovals
        })
      : removeDiscountCodeFromCartsSucceeded({
          requestedActionId,
          codeId,
          customOrderTypes: successfulRemovals
        })
  );
}

export default removeDiscountCodeFromCartsSaga;
